import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Image, Text, TextInput, Title } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation } from '@apollo/client';

import { REQUEST_LOGIN_OTP } from '../../apollo/mutations/auth';
import AppleLogo from '../../assets/app-store-logos/apple.png';
import GoogleLogo from '../../assets/app-store-logos/google.png';
import HuaweiLogo from '../../assets/app-store-logos/huawei.png';
import logo from '../../assets/cb-logo.svg';
import client from '../../apollo';
import LoginOtpForm from './LoginOtpForm';

interface Props {
    isStoreManager: boolean;
}

function LoginForm({ isStoreManager }: Props) {
    const [loading, setLoading] = useState(false);
    const [otpStep, setOtpStep] = useState(false);
    const [currentUsername, setCurrentUsername] = useState('');
    const [requestError, setRequestError] = useState<string | null>(null);

    const { state: locationState } = useLocation();

    const [requestLoginOtp, { error: otpRequestError }] =
        useMutation(REQUEST_LOGIN_OTP);

    useEffect(() => {
        client.resetStore();
    }, []);

    const handleRequestOtp = useCallback(
        async (username: string) => {
            setLoading(true);
            try {
                const { data } = await requestLoginOtp({
                    variables: {
                        input: {
                            username: username.trim(),
                        },
                    },
                });
                if (data?.requestLoginOtp?.successful) {
                    setCurrentUsername(username.trim());
                    setOtpStep(true);
                    setRequestError(null); // Clear any previous errors
                } else {
                    // Handle business logic failure
                    setRequestError('Failed to send OTP. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        },
        [requestLoginOtp],
    );

    const handleBack = useCallback(() => {
        setOtpStep(false);
        setCurrentUsername('');
    }, []);

    const form = useForm({
        initialValues: {
            username: locationState?.username || '',
        },

        validate: {
            username: value => (!value ? 'Username is required' : null),
        },
    });

    if (otpStep) {
        return <LoginOtpForm username={currentUsername} onBack={handleBack} />;
    }

    return (
        <form
            onSubmit={form.onSubmit(values =>
                handleRequestOtp(values.username),
            )}
            className="space-y-8 "
        >
            <Image src={logo} alt="Logo" width={250} />
            {!isStoreManager && (
                <>
                    <div className="flex flex-col gap-3">
                        <Title className="text-4xl">Log in</Title>
                        <Text>
                            Enter your username and we will send you an OTP via
                            WhatsApp
                        </Text>
                    </div>
                    {!!(otpRequestError || requestError) && (
                        <Alert
                            icon={<IconAlertCircle size="1rem" />}
                            title="Request Failed"
                            color="red"
                        >
                            {requestError ?? otpRequestError?.message}
                        </Alert>
                    )}
                    <TextInput
                        id="usernameInput"
                        label="Username"
                        placeholder="doejane"
                        type="text"
                        size="lg"
                        classNames={{
                            input: 'bg-gray-100 p-6 h-14',
                            label: 'font-bold pb-3',
                        }}
                        {...form.getInputProps('username')}
                    />
                    <Button
                        loading={loading}
                        type="submit"
                        size="lg"
                        radius="xl"
                        className="h-14"
                    >
                        Send OTP
                    </Button>
                </>
            )}
            <div className="pb-8">
                <h4 className="text-center">
                    Download the Cashbuild Operations Manager App
                </h4>
                <div className="flex flex-row items-center justify-center gap-4 mt-2">
                    <Link
                        to="https://apps.apple.com/us/app/cashbuild-operations-manager/id6480587686"
                        target="_blank"
                    >
                        <Image
                            src={AppleLogo}
                            width={120}
                            className="w-32 h-auto"
                        />
                    </Link>
                    <Link
                        to="https://play.google.com/store/apps/details?id=com.cashbuild.operationsapp"
                        target="_blank"
                    >
                        <Image
                            src={GoogleLogo}
                            width={120}
                            className="w-32 h-auto"
                        />
                    </Link>
                    <Link
                        to="https://appgallery.huawei.com/app/C111427847"
                        target="_blank"
                    >
                        <Image
                            src={HuaweiLogo}
                            width={120}
                            className="w-32 h-auto"
                        />
                    </Link>
                </div>
            </div>
        </form>
    );
}

export default LoginForm;
